import { useSuspenseQuery } from '@apollo/client';
import { Suspense, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { ErrorBoundary } from '@xing-com/crate-core-error-boundary';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { PageLayout } from '@xing-com/crate-people-directory-page-layout';
import { TopLevelPageLink } from '@xing-com/crate-people-directory-top-level-page-link';
import {
  UserMessage,
  UserMessageType,
} from '@xing-com/crate-people-directory-user-message';
import { Grid, Row } from '@xing-com/xing-grid';

import { ContentSpacer, StyledHeadline } from './cities-page.styles';
import { SEO_URL_META_DATA } from './constants';
import { CitiesPageDocument } from './graphql/cities-page.query.gql-types';

type BasicPageLayoutProps = {
  seoTitle: string;
  seoDescription: string;
  index: boolean;
  seoUrl: string;
  pageName: string;
};

const CitiesListingPageInner: React.FC<BasicPageLayoutProps> = (
  basicPageLayoutProps
) => {
  const { formatMessage } = useIntl();
  const { isLoggedOut } = useLoginState();
  const { data, error } = useSuspenseQuery(CitiesPageDocument, {
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (!isLoggedOut) {
      window.location.assign('https://www.xing.com/search/members');
    }
  }, [isLoggedOut]);

  if (!isLoggedOut) return;
  if (error || !data) throw error;

  const { cityTopLevelPages = [] } = data?.peopleCityTopLevelPage || {};

  const sanitizedCityTopLevelPages = cityTopLevelPages?.map(
    (cityTopLevelPage) => {
      return {
        cityName: cityTopLevelPage?.cityName || '',
        url: `${cityTopLevelPage?.url}` || '',
        exampleNames: cityTopLevelPage?.exampleNames?.map((exampleName) => {
          return {
            url: `${exampleName?.url}` || '',
            name: exampleName?.name || '',
          };
        }),
      };
    }
  );

  if (!data?.peopleCityTopLevelPage || !cityTopLevelPages?.length) {
    return <EmptyStateFallback {...basicPageLayoutProps} />;
  }

  return (
    <PageLayout {...basicPageLayoutProps}>
      <ContentSpacer>
        <StyledHeadline
          size="xxlarge"
          // oxlint-disable-next-line jsx_a11y/prefer-tag-over-role, jsx_a11y/role-has-required-aria-props
          role="heading"
        >
          {formatMessage({ id: 'PD_CITIES_PEOPLE_IN_CITIES_HEADLINE' })}
        </StyledHeadline>
        <Grid>
          <Row>
            {sanitizedCityTopLevelPages?.map(
              ({ cityName, url, exampleNames }, index) => (
                <TopLevelPageLink
                  key={`${cityName}-${index}`}
                  title={cityName}
                  url={url}
                  exampleNames={exampleNames}
                />
              )
            )}
          </Row>
        </Grid>
      </ContentSpacer>
    </PageLayout>
  );
};

const EmptyStateFallback: React.FC<BasicPageLayoutProps> = (
  basicPageLayoutProps
) => {
  return (
    <PageLayout {...basicPageLayoutProps}>
      <UserMessage type={UserMessageType.empty} />
    </PageLayout>
  );
};

const ErrorFallback: React.FC<BasicPageLayoutProps> = (
  basicPageLayoutProps
) => {
  return (
    <PageLayout {...basicPageLayoutProps}>
      <UserMessage type={UserMessageType.error} />
    </PageLayout>
  );
};

export const CitiesListingPage: React.FC = () => {
  const { formatMessage } = useIntl();
  const basicPageLayoutProps = {
    seoTitle: formatMessage({ id: 'PD_CITIES_TOP_CITIES_TITLE' }),
    index: true,
    seoDescription: formatMessage({
      id: 'PD_CITIES_TOP_CITIES_META_DESCRIPTION',
    }),
    seoUrl: SEO_URL_META_DATA,
    pageName: 'cities',
  };

  return (
    <ErrorBoundary onError={() => <ErrorFallback {...basicPageLayoutProps} />}>
      <Suspense fallback={null}>
        <CitiesListingPageInner {...basicPageLayoutProps} />
      </Suspense>
    </ErrorBoundary>
  );
};
